import { Kundetype, Maalepunkt, ProduktSpesifikasjon, ValidationError } from "codegen";
import { useEffect, useState } from "react";
import styled from "styled-components";

import Button from "@ntemarked/mittne-components/dist/Button/Button";
import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";
import TabSelect from "@ntemarked/mittne-components/dist/TabSelect/TabSelect";

import ElHubHealthCheckAlert from "components/organisms/ElhubHealthCheckAlert";
import TextField from "components/organisms/form/TextField";
import { useStrombestillingApiMutation, useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { MAALEPUNKT_ID_PREFIX, isValidMaalepunktId } from "utils/Maalepunkt";
import { validateFodselsnummer } from "utils/Utils";

export type MaalerMedValidering = Maalepunkt & { validation: { isValid: boolean; message: string } };

interface MeterSearchProps {
	product?: ProduktSpesifikasjon;
	/**
	 * Kundetype å søke som når det ikke er valgt produkt. Uten produkt valideres ikke målepunktene
	 * mot tariffreglene - det gjør backend selv når ordren sendes inn.
	 */
	kundetype?: Kundetype;
	fodselsnummer?: string;
	orgnummer?: string;
	selectedMeter: MaalerMedValidering | null;
	setSelectedMeter: (meter: MaalerMedValidering | null) => void;
}

export default function MeterSearch({ product, kundetype, selectedMeter, setSelectedMeter, fodselsnummer, orgnummer }: Readonly<MeterSearchProps>) {
	const [isMeteringpointIdSearch, setIsMeteringpointIdSearch] = useState(false);
	const [meterNumber, setMeterNumber] = useState<string | null>(null);
	const [postNumber, setPostNumber] = useState<string | null>(null);
	const [meterPointId, setMeterPointId] = useState<string | null>(null);
	const [availableMeters, setAvailableMeters] = useState<MaalerMedValidering[]>([]);
	const [searchMeterMessage, setSearchMeterMessage] = useState<string | null>(null);

	const tariffId = product?.tariffId ?? "";

	useEffect(() => {
		setAvailableMeters([]);
		setSelectedMeter(null);
		setSearchMeterMessage(null);
		// Om vi legger til setSelectedMeter i dependency array vil målervisningen resettes når vi trykker på den
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product?.tariffId, fodselsnummer, orgnummer]);

	const isCompany = (product?.kundetype ?? kundetype) === Kundetype.Bedrift;
	const ownerId = isCompany ? orgnummer : fodselsnummer;

	// Søk
	const { refetch: searchMeterNumber, isFetching: isLoadingSearchMeterNumber } = useStrombestillingApiQuery<Maalepunkt[]>(
		// meterNumber kan være enten målernummer eller målepunktID, blir håndtert i backend
		`/maalepunkter?maalernummer=${encodeURIComponent(meterNumber || "")}&postnummer=${encodeURIComponent(postNumber || "")}` +
			(ownerId ? `&eierId=${encodeURIComponent(ownerId)}` : ""),
		["maalepunkter", ownerId ?? "", meterNumber ?? "", postNumber ?? ""],
		false // Hent bare ved knappetrykk
	);
	const { refetch: searchMeterPointId, isFetching: isLoadingSearchMeterPointId } = useStrombestillingApiQuery<Maalepunkt[]>(
		// meterNumber kan være enten målernummer eller målepunktID, blir håndtert i backend
		`/maalepunkter?maalernummer=${encodeURIComponent(meterPointId || "")}` + (ownerId ? `&eierId=${encodeURIComponent(ownerId)}` : ""),
		["maalepunkter", ownerId ?? "", meterPointId ?? ""],
		false // Hent bare ved knappetrykk
	);

	type SearchFunction = () => Promise<{ data: Maalepunkt[] | undefined }>;
	async function handleSearch(searchFunction: SearchFunction, validationCondition: boolean, validationMessage: string, notFoundMessage: string) {
		if (isAnyLoading) return;
		setSearchMeterMessage(null);
		setAvailableMeters([]);
		setSelectedMeter(null);

		if (!product && !kundetype) {
			setSearchMeterMessage("Vennligst velg et produkt for validering før du søker etter måler.");
			return;
		}

		if (validationCondition) {
			setSearchMeterMessage(validationMessage);
			return;
		}

		if (!isCompany && !fodselsnummer) {
			setSearchMeterMessage("Finn kunden før du søker etter måler. Skriv inn fødselsnummer, eller søk etter personen med navn og fødselsdato.");
			return;
		}

		if (!isCompany && !validateFodselsnummer(fodselsnummer)) {
			setSearchMeterMessage("Fødselsnummeret til kunden er ugyldig. Rett det før du søker etter måler.");
			return;
		}

		if (isCompany && !orgnummer) {
			setSearchMeterMessage("Søk etter bedrift først");
			return;
		}

		try {
			const response = await searchFunction();
			const meters = response.data ?? [];
			if (meters && meters.length > 0) {
				const metersWithValidation = await addMeterValidation(meters);
				setAvailableMeters(metersWithValidation);
				if (metersWithValidation.length === 1 && metersWithValidation[0].validation.isValid) setSelectedMeter(metersWithValidation[0]);
			} else {
				setSearchMeterMessage(notFoundMessage);
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			setSearchMeterMessage(`Noe gikk galt ved søk etter måler, feilmelding: ${errorMessage}`);
		}
	}

	// Validering
	const { mutateAsync: validateMeters, isPending: isLoadingSearchValidMeter } = useStrombestillingApiMutation(
		"/tariffregler/valider",
		["tariffregler", tariffId, availableMeters.map((m) => m.maalepunktId).join(",")],
		"POST"
	);
	async function addMeterValidation(meters: Maalepunkt[]): Promise<MaalerMedValidering[]> {
		if (meters.length === 0) return [];
		if (!tariffId) return meters.map((m) => ({ ...m, validation: { isValid: true, message: "" } }));
		const validationResponse = await validateMeters({ tariffId, maalepunktIder: meters.map((m) => m.maalepunktId) });
		if (validationResponse.ok) {
			return meters.map((m) => ({
				...m,
				validation: {
					isValid: true,
					message: `Måler er godkjent for ${tariffId}.`,
				},
			}));
		}
		const validation = await validationResponse.json();
		return meters.map((m) => {
			const maalerValideringsfeil = validation.errors.find((e: ValidationError) => e.values?.maalerpunktId === m.maalepunktId);
			return {
				...m,
				validation: {
					isValid: false,
					message: maalerValideringsfeil?.message || validation.errors[0]?.message || "Ukjent valideringsfeil for måler.",
				},
			};
		});
	}

	const isAnyLoading = isLoadingSearchMeterNumber || isLoadingSearchMeterPointId || isLoadingSearchValidMeter;

	return (
		<SearchWrapper direction={Direction.column} gap="1rem">
			<ElHubHealthCheckAlert />

			<TabSelectWrapper>
				<TabSelect
					options={[
						{ value: "false", label: "Målernummer" },
						{ value: "true", label: "Målepunkt-ID" },
					]}
					value={isMeteringpointIdSearch + ""}
					onChange={(v) => {
						setIsMeteringpointIdSearch(v === "true");
						setSelectedMeter(null);
					}}
				/>
			</TabSelectWrapper>

			{!isMeteringpointIdSearch ? (
				<SearchMeterNumberWrapper>
					<TextField
						label="Målernummer"
						description="Målernummer står på måleren. Det er ikke unikt, så også postnummer trengs for søket"
						type="text"
						autoComplete="off"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMeterNumber(e.target.value.trim())}
					/>
					<TextField
						label="Postnummer"
						type="text"
						autoComplete="off"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostNumber(e.target.value.trim())}
					/>
					<SearchButton
						label={isLoadingSearchMeterNumber ? "Søker..." : "Søk etter måler"}
						type="button"
						loading={isLoadingSearchMeterNumber}
						onClick={() =>
							handleSearch(
								searchMeterNumber,
								!meterNumber || !postNumber,
								"Vennligst oppgi målernummer og postnummer for å søke etter måler.",
								"Fant ingen treff på målernummer."
							)
						}
					/>
				</SearchMeterNumberWrapper>
			) : (
				<SearchMeterWrapper>
					<TextField
						label="Målepunkt-ID"
						description="Målepunkt-ID står på faktura eller på Mine Sider i Elhub. Målepunkt-ID starter alltid på 7070575000 og består alltid av 18 siffer"
						type="text"
						autoComplete="off"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMeterPointId(e.target.value.trim())}
					/>
					<SearchButton
						label={isLoadingSearchMeterPointId ? "Søker..." : "Søk etter måler"}
						type="button"
						loading={isLoadingSearchMeterPointId}
						onClick={() =>
							handleSearch(
								searchMeterPointId,
								!isValidMaalepunktId(meterPointId ?? ""),
								!meterPointId
									? "Vennligst oppgi målepunkt-ID for å søke etter måler."
									: !meterPointId.startsWith(MAALEPUNKT_ID_PREFIX)
										? `Målepunkt-ID starter ikke med ${MAALEPUNKT_ID_PREFIX}`
										: "Ugyldig målepunkt-ID. Sjekk at den har 18 siffer og er skrevet riktig.",
								"Fant ingen treff på målepunkt-ID."
							)
						}
					/>
				</SearchMeterWrapper>
			)}

			{availableMeters.length > 0 ? (
				<fieldset>
					<legend>Velg måler fra søkeresultat:</legend>
					{availableMeters.map((meter) => (
						<div key={meter.maalepunktId}>
							<label htmlFor={`meter-${meter.maalepunktId}`}>
								<input
									type="radio"
									id={`meter-${meter.maalepunktId}`}
									name="selectedMeter"
									value={meter.maalepunktId}
									disabled={meter.validation && !meter.validation.isValid}
									checked={selectedMeter?.maalepunktId === meter.maalepunktId}
									onChange={() => {
										setSelectedMeter(meter);
									}}
								/>
								Måler med målernummer {meter.maalernummer} og målepunkt-ID {meter.maalepunktId} på {meter.adresse?.asString}.{" "}
								<span style={{ color: meter.validation.isValid ? "seagreen" : "firebrick" }}>{meter.validation.message}</span>
							</label>
						</div>
					))}
				</fieldset>
			) : (
				searchMeterMessage && <p>{searchMeterMessage}</p>
			)}
		</SearchWrapper>
	);
}

export const TabSelectWrapper = styled.div`
	max-width: fit-content;
`;

export const SearchWrapper = styled(Flex)`
	padding: 1rem;
	border: 1px solid var(--nte-graa-400);
	border-radius: 4px;
`;

export const SearchMeterWrapper = styled.div`
	display: grid;
	grid-template-columns: 4fr auto;
	gap: 0.5rem;
	align-items: start;
`;

const SearchMeterNumberWrapper = styled(SearchMeterWrapper)`
	grid-template-columns: 4fr 1fr auto;
`;

export const SearchButton = styled(Button)`
	height: fit-content;
	margin-top: 1.2rem;
	width: 11.5rem;
`;
